import { isSameDay, parseISO } from 'date-fns';
import { TreePalm } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatHoursToTimeString } from '@/helpers';

import { getProgressColor } from '../../helpers/progress-color.helper';
import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';
import { sortTimeOffSummaryItems } from '../../helpers/time-off-sort.helper';
import { MonthCalendarDay, TimeOffRequestSummaryStatus } from '../../types/time-tracking';

interface MonthDayCellProps {
  /** The calendar day's data, or null for leading/trailing padding cells. */
  day: MonthCalendarDay | null;
  /** Calendar date of this cell (always present; even padding cells need a date). */
  date: Date;
  isCurrentMonth: boolean;
  isWeekend: boolean;
  selectedDay: Date | null;
  onSelect: (date: Date) => void;
}

const MAX_DOTS = 6;

export function MonthDayCell({
  day,
  date,
  isCurrentMonth,
  isWeekend,
  selectedDay,
  onSelect,
}: MonthDayCellProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const today = new Date();
  const isToday = isSameDay(date, today);
  const isSelected = selectedDay ? isSameDay(date, selectedDay) : false;
  const dayNumber = date.getDate();

  if (!isCurrentMonth) {
    return <div className="border-l border-t bg-muted/30" />;
  }

  const tracked = day?.trackedHours ?? 0;
  const totalCap = day?.totalCapacityHours ?? 0;
  const effectiveCap = day?.effectiveCapacityHours ?? 0;
  // Members can't take time off on weekends — never render time-off there even if
  // the backend returns it.
  const showTimeOff = !isWeekend;
  const timeOffEntries = showTimeOff ? (day?.timeOff ?? []) : [];
  const timeOffTotalHours = timeOffEntries.reduce((sum, t) => sum + t.totalHours, 0);
  // time-off hours (pending + approved) count toward the displayed numerator.
  const displayedHours = tracked + timeOffTotalHours;
  const hasTimeOff = timeOffEntries.length > 0;
  const isFullTimeOff = showTimeOff && !!day?.isFullDayTimeOff;
  const isPartialTimeOff = hasTimeOff && !isFullTimeOff;

  // The cell bottom is filled with bands stacked from the bottom up:
  // regular tracked → over-cap tracked (purple) → time-off entries (sorted with
  // APPROVED right above tracked and PENDING on top, matching the week-view
  // stack order).
  //
  // Sizing: when `tracked + time-off ≤ totalCap`, the fill region's height is
  // `(used / totalCap) * 100%` so the cell fills exactly to `used/cap`.
  // When the user overruns capacity, the fill region is clamped to 100% and
  // bands share the height proportionally to `used`. Either way bands are
  // proportional to hours.
  const sortedTimeOff = sortTimeOffSummaryItems(timeOffEntries);
  const usedHours = tracked + timeOffTotalHours;
  const fillHeightPct =
    totalCap > 0 ? Math.min(100, (usedHours / totalCap) * 100) : usedHours > 0 ? 100 : 0;
  // Split tracked into the "fits under remaining capacity" part and the
  // overflow part. time-off sits at the top so it's not part of the over-cap
  // calculation — tracked overflow is the chunk that wouldn't fit alongside
  // time-off inside capacity.
  const trackedRegular = Math.min(tracked, Math.max(0, totalCap - timeOffTotalHours));
  const trackedOver = Math.max(0, tracked - trackedRegular);
  // When the user has overtracked, the regular (under-cap) portion should
  // render as fully filled green — the purple overflow band already signals
  // the over-capacity state, and matching the daily-overview progress bar's
  // semantics (green main + purple overtime) keeps the two views consistent.
  const COLOR_GREEN_FILL = '#22C55E';
  const progressColor =
    tracked > 0
      ? trackedOver > 0
        ? COLOR_GREEN_FILL
        : getProgressColor(calculateProgressPercentage(tracked, effectiveCap))
      : null;
  const OVERFLOW_PURPLE = '#9333EA';

  let hoursLabelClass = '';
  if (totalCap > 0 && tracked > 0) {
    const ratio = tracked / totalCap;
    if (ratio > 1.1) hoursLabelClass = 'text-destructive';
    else if (ratio < 0.9) hoursLabelClass = 'text-warning';
  }

  const dots = day?.topProjects ?? [];

  return (
    <button
      type="button"
      onClick={() => onSelect(date)}
      className={`group relative flex h-full min-h-[110px] flex-col items-stretch gap-1 overflow-hidden border-l border-t p-2 text-left transition-colors hover:bg-muted/20 ${
        isWeekend ? 'bg-muted/30' : 'bg-card'
      } ${isSelected ? 'shadow-[inset_0_0_0_2px_hsl(var(--foreground))]' : ''}`}
    >
      {/* Cell background fill — bottom-anchored stack of bands:
          regular tracked → over-cap tracked (purple) → time-off entries.
          Bands are sized by flex-grow proportional to hours, and the whole
          stack's height equals `min(used/cap, 1)` of the cell.

          The container is `flex-col-reverse`, so DOM order maps to bottom→top.
          Time-off is rendered APPROVED-first so that — after the reverse —
          approved sits below pending, matching the week-view strip. */}
      {usedHours > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col-reverse"
          style={{ height: `${fillHeightPct}%` }}
        >
          {trackedRegular > 0 && progressColor && (
            <div
              style={{
                flex: `${trackedRegular} 0 0`,
                background: progressColor,
                opacity: 0.22,
              }}
            />
          )}
          {trackedOver > 0 && (
            <div
              style={{
                flex: `${trackedOver} 0 0`,
                background: OVERFLOW_PURPLE,
                opacity: 0.32,
              }}
            />
          )}
          {[...sortedTimeOff].reverse().map((entry, idx) => {
            const isApproved = entry.status === TimeOffRequestSummaryStatus.APPROVED;
            return (
              <div
                key={idx}
                className={isApproved ? 'time-off-approved' : 'time-off-pending'}
                style={{ flex: `${entry.totalHours} 0 0` }}
              />
            );
          })}
        </div>
      )}

      {/* Row 1: day number + time-off badge */}
      <div className="relative z-[1] flex items-center justify-between">
        <span
          className={`inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1 text-xs font-semibold tabular-nums ${
            isToday
              ? 'bg-foreground text-background'
              : isWeekend
                ? 'text-muted-foreground/70'
                : 'text-foreground'
          }`}
        >
          {dayNumber}
        </span>
        {isFullTimeOff && (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-time-off-text">
            <TreePalm className="h-3 w-3" aria-hidden="true" />
            {sortedTimeOff[0]?.status === TimeOffRequestSummaryStatus.PENDING
              ? t('monthView.day.fullTimeOffPendingBadge')
              : t('monthView.day.fullTimeOffApprovedBadge')}
          </span>
        )}
        {isPartialTimeOff && (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-time-off-text-soft">
            <TreePalm className="h-3 w-3" aria-hidden="true" />
            {t('monthView.day.partialTimeOffBadge')}
          </span>
        )}
      </div>

      {/* Row 2: (tracked + time-off) / raw capacity. time-off hours (pending +
          approved) count toward the numerator — so e.g. a day with 2h pending
          time-off and nothing tracked reads "2h / 8h" rather than "0h / 8h".
          The over/under color stays keyed on actual tracked work only, so
          time-off never triggers a warning color. Hidden when full-day
          time-off covers the whole day and nothing is tracked — the OOO label
          already communicates that, so "0h / 0h" would be redundant noise. */}
      <div className="relative z-[1]">
        {isWeekend || (isFullTimeOff && tracked === 0) ? null : displayedHours > 0 ? (
          <span className={`text-[13px] font-semibold tabular-nums ${hoursLabelClass}`}>
            {t('monthView.day.hoursOfCapacity', {
              tracked: formatHoursToTimeString(displayedHours),
              capacity: formatHoursToTimeString(totalCap),
            })}
          </span>
        ) : (
          <span className="text-[13px] tabular-nums text-muted-foreground/70">
            {t('monthView.day.hoursOfCapacity', {
              tracked: formatHoursToTimeString(0),
              capacity: formatHoursToTimeString(totalCap),
            })}
          </span>
        )}
      </div>

      {/* Row 3: project color dots */}
      {dots.length > 0 && (
        <div className="relative z-[1] mt-auto flex items-center gap-[3px]">
          {dots.slice(0, MAX_DOTS).map(dot => (
            <span
              key={dot.projectId}
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: dot.colorHex }}
            />
          ))}
          {dots.length > MAX_DOTS && (
            <span className="text-[9px] leading-none text-muted-foreground">
              {t('monthView.day.dotOverflow', { count: dots.length - MAX_DOTS })}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

/** Build the calendar cell list for a given month (leading/trailing padding included). */
export interface CalendarCell {
  date: Date;
  isCurrentMonth: boolean;
  day: MonthCalendarDay | null;
}

export const buildCalendarCells = (
  monthStart: Date,
  monthEnd: Date,
  days: MonthCalendarDay[]
): CalendarCell[] => {
  // Mon=0, Tue=1 ... Sun=6 (we use Monday-first weeks).
  const startDow = (monthStart.getDay() + 6) % 7;
  const endDow = (monthEnd.getDay() + 6) % 7;

  const dayMap = new Map<string, MonthCalendarDay>();
  for (const d of days) {
    const parsed = parseISO(d.date);
    dayMap.set(`${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`, d);
  }

  const cells: CalendarCell[] = [];

  // Leading padding
  for (let i = startDow; i > 0; i--) {
    const padDate = new Date(monthStart);
    padDate.setDate(padDate.getDate() - i);
    cells.push({ date: padDate, isCurrentMonth: false, day: null });
  }

  // Current month
  const cursor = new Date(monthStart);
  while (cursor <= monthEnd) {
    const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
    cells.push({
      date: new Date(cursor),
      isCurrentMonth: true,
      day: dayMap.get(key) ?? null,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  // Trailing padding to fill the last week
  const trailing = 6 - endDow;
  for (let i = 1; i <= trailing; i++) {
    const padDate = new Date(monthEnd);
    padDate.setDate(padDate.getDate() + i);
    cells.push({ date: padDate, isCurrentMonth: false, day: null });
  }

  return cells;
};
